import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Slider,
  Tooltip,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService, { CRPromptTemplate, CRModel, Dataset } from '../services/api';
import Header from '../components/Header';
import CRDatasetUpload from '../components/CRDatasetUpload';
import { RENAISSANCE_COLORS } from '../utils';

const CREvalRun: React.FC = () => {
  const navigate = useNavigate();

  // State management
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dataset state
  const [selectedDataset, setSelectedDataset] = useState<string>('');
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [validationStatus, setValidationStatus] = useState<'pending' | 'valid' | 'invalid'>('pending');

  // Configuration state
  const [batchSize, setBatchSize] = useState(150);
  const [maxConcurrent, setMaxConcurrent] = useState(150);
  const [promptTemplate, setPromptTemplate] = useState('prompt_6'); // Default recommended template
  const [temperature, setTemperature] = useState(0.0);
  const [filterQuery, setFilterQuery] = useState('');
  const [samplingEnabled, setSamplingEnabled] = useState(false);
  const [sampleSize, setSampleSize] = useState(1000);

  // Model selection state
  const [modelId, setModelId] = useState('');
  const [models, setModels] = useState<CRModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);

  // Thinking tokens state
  const [thinkingEnabled, setThinkingEnabled] = useState(false);
  const [thinkingBudget, setThinkingBudget] = useState(1024);

  // Prompt templates
  const [promptTemplates, setPromptTemplates] = useState<CRPromptTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Create template dialog state
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDesc, setNewTemplateDesc] = useState('');
  const [newTemplateContent, setNewTemplateContent] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Load models on mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        setLoadingModels(true);
        const response = await apiService.listCRModels();
        if (response.status === 'success') {
          setModels(response.models);
          // Pre-select the default (first) model
          if (response.models.length > 0 && !modelId) {
            setModelId(response.models[0].id);
          }
        }
      } catch (err: any) {
        console.error('Error loading models:', err);
      } finally {
        setLoadingModels(false);
      }
    };
    fetchModels();
    // Runs once on mount; the modelId guard only pre-selects a default when
    // none is set yet, so modelId is intentionally not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load prompt templates on mount
  useEffect(() => {
    const fetchPromptTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const response = await apiService.listCRPromptTemplates();
        if (response.status === 'success') {
          setPromptTemplates(response.templates);
        } else {
          console.error('Failed to load prompt templates');
          toast.error('Failed to load prompt templates');
        }
      } catch (err: any) {
        console.error('Error loading prompt templates:', err);
        toast.error('Failed to load prompt templates');
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchPromptTemplates();
  }, []);

  // Load datasets
  useEffect(() => {
    const loadDatasets = async () => {
      try {
        setLoadingDatasets(true);
        const response = await apiService.listDatasets('parquet');
        if (response.status === 'success') {
          setDatasets(response.datasets || []);
        } else {
          setDatasets([]);
        }
      } catch (err: any) {
        console.error('Failed to load datasets:', err);
        setDatasets([]);
      } finally {
        setLoadingDatasets(false);
      }
    };
    loadDatasets();
  }, []);

  // Validate dataset when selected
  useEffect(() => {
    if (selectedDataset) {
      validateDataset(selectedDataset);
    } else {
      setValidationStatus('pending');
    }
  }, [selectedDataset]);

  const validateDataset = async (s3Key: string) => {
    try {
      const response = await apiService.validateCRDataset(s3Key);
      if (response.valid) {
        setValidationStatus('valid');
      } else {
        setValidationStatus('invalid');
        toast.warning(`Dataset validation failed: Missing columns ${response.missing_columns?.join(', ')}`);
      }
    } catch (err: any) {
      console.error('Dataset validation error:', err);
      setValidationStatus('invalid');
      toast.error('Failed to validate dataset');
    }
  };

  const handleRunEvaluation = async () => {
    if (!selectedDataset) {
      toast.error('Please select a dataset');
      return;
    }

    if (validationStatus !== 'valid') {
      toast.error('Please select a valid dataset or map columns');
      return;
    }

    setError(null);
    try {
      setIsRunning(true);

      const response = await apiService.createCREvaluation({
        dataset_s3_path: selectedDataset,
        batch_size: batchSize,
        max_concurrent: maxConcurrent,
        prompt_template: promptTemplate,
        temperature: temperature,
        filter_query: filterQuery || undefined,
        sample_enabled: samplingEnabled,
        sample_size: samplingEnabled ? sampleSize : undefined,
        output_prefix: 'cr_results',
        model_id: modelId || undefined,
        thinking_enabled: thinkingEnabled || undefined,
        thinking_budget: thinkingEnabled ? thinkingBudget : undefined,
      });

      if (response.status !== 'success') {
        setError((response as any)?.error || 'Failed to create CR evaluation job');
        toast.error((response as any)?.error || 'Failed to create CR evaluation job');
      } else {
        toast.success('CR evaluation job created! Redirecting to job details...');
        navigate(`/evaluations/${response.job_id}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create CR evaluation job');
      toast.error(err.message || 'Failed to create CR evaluation job');
      setIsRunning(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (!newTemplateContent.trim()) {
      toast.error('Template content is required');
      return;
    }
    try {
      setSavingTemplate(true);
      const response = await apiService.createCRPromptTemplate({
        name: newTemplateName.trim(),
        description: newTemplateDesc.trim(),
        content: newTemplateContent.trim(),
      });
      if (response.status === 'success') {
        toast.success(response.message);
        // Refresh template list and select the new one
        const templatesResponse = await apiService.listCRPromptTemplates();
        if (templatesResponse.status === 'success') {
          setPromptTemplates(templatesResponse.templates);
        }
        setPromptTemplate(newTemplateName.trim());
        setCreateTemplateOpen(false);
        setNewTemplateName('');
        setNewTemplateDesc('');
        setNewTemplateContent('');
      } else {
        toast.error((response as any)?.error || 'Failed to create template');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to create template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getValidationStatusColor = () => {
    switch (validationStatus) {
      case 'valid':
        return 'success.main';
      case 'invalid':
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };

  const getValidationStatusText = () => {
    switch (validationStatus) {
      case 'valid':
        return '✓ Ready';
      case 'invalid':
        return '⚠ Mapping Required';
      default:
        return 'Select dataset';
    }
  };

  return (
    <div style={{
      backgroundColor: RENAISSANCE_COLORS.beige,
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
    }}>
      <Header />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main' }}>
            CR Evaluation Run
          </Typography>

          <Typography variant="body1" paragraph>
            Run a Constructed Response (CR) evaluation with customizable prompts, scoring parameters,
            and comprehensive QWK analysis including grade-level and characteristic breakdowns.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Dataset Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Dataset Selection
              </Typography>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <FormControl fullWidth disabled={loadingDatasets}>
                    <InputLabel>Select Dataset</InputLabel>
                    <Select
                      value={selectedDataset}
                      onChange={(e) => setSelectedDataset(e.target.value)}
                      label="Select Dataset"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {datasets.map((dataset) => (
                        <MenuItem key={dataset.s3_key || dataset.key} value={dataset.s3_key || dataset.key}>
                          {dataset.filename} ({formatFileSize(dataset.size)})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ color: getValidationStatusColor() }}>
                      Validation Status: <strong>{getValidationStatusText()}</strong>
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {validationStatus === 'invalid' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This dataset requires column mapping. Please map the columns or select a different dataset.
                </Alert>
              )}

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>

              {/* Dataset Upload */}
              <CRDatasetUpload
                onUploadComplete={(s3Key) => {
                  setSelectedDataset(s3Key);
                  // Refresh datasets list
                  const loadDatasets = async () => {
                    try {
                      const response = await apiService.listDatasets('parquet');
                      if (response.status === 'success') {
                        setDatasets(response.datasets || []);
                      }
                    } catch (err: any) {
                      console.error('Failed to refresh datasets:', err);
                    }
                  };
                  loadDatasets();
                }}
              />
            </CardContent>
          </Card>

          {/* Configuration */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configuration
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Batch Size"
                      type="number"
                      value={batchSize}
                      onChange={(e) => setBatchSize(Number(e.target.value))}
                      inputProps={{ min: 1, max: 200 }}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Max Concurrent Requests"
                      type="number"
                      value={maxConcurrent}
                      onChange={(e) => setMaxConcurrent(Number(e.target.value))}
                      inputProps={{ min: 1, max: 200 }}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <FormControl fullWidth disabled={loadingTemplates}>
                      <InputLabel>Prompt Template</InputLabel>
                      <Select
                        value={promptTemplate}
                        onChange={(e) => setPromptTemplate(e.target.value)}
                        label="Prompt Template"
                        renderValue={(value) => {
                          if (loadingTemplates) return 'Loading…';
                          const tmpl = promptTemplates.find((t) => t.name === value);
                          return tmpl
                            ? `${tmpl.name} - ${tmpl.description}${tmpl.name === 'prompt_6' ? ' (Recommended)' : ''}`
                            : value;
                        }}
                      >
                        {loadingTemplates ? (
                          <MenuItem disabled value={promptTemplate}>
                            <CircularProgress size={16} sx={{ mr: 1 }} /> Loading templates…
                          </MenuItem>
                        ) : (
                          promptTemplates.map((template) => (
                            <MenuItem key={template.name} value={template.name}>
                              {template.name} - {template.description}
                              {template.name === 'prompt_6' && ' (Recommended)'}
                              {(template as any).is_custom && ' ★'}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                    <Tooltip title="Create a new prompt template">
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateTemplateOpen(true)}
                        sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
                      >
                        New
                      </Button>
                    </Tooltip>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth disabled={loadingModels}>
                    <InputLabel>Model</InputLabel>
                    <Select
                      value={modelId}
                      onChange={(e) => setModelId(e.target.value)}
                      label="Model"
                    >
                      {loadingModels ? (
                        <MenuItem disabled value="">
                          <CircularProgress size={16} sx={{ mr: 1 }} /> Loading models…
                        </MenuItem>
                      ) : (
                        models.map((model) => (
                          <MenuItem key={model.id} value={model.id}>
                            {model.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ px: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography gutterBottom>
                        Temperature: {temperature.toFixed(1)}
                      </Typography>
                      <Tooltip title="Controls randomness in responses. 0.0 = deterministic, 1.0 = maximum randomness">
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Slider
                      value={temperature}
                      onChange={(_, value) => setTemperature(value as number)}
                      min={0}
                      max={1}
                      step={0.1}
                      marks={[
                        { value: 0, label: '0.0' },
                        { value: 0.5, label: '0.5' },
                        { value: 1, label: '1.0' }
                      ]}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={thinkingEnabled}
                          onChange={(e) => setThinkingEnabled(e.target.checked)}
                        />
                      }
                      label="Extended Thinking"
                    />
                    <Tooltip title="Enables Claude's extended thinking (reasoning) mode. Requires a compatible model and increases token usage.">
                      <IconButton size="small">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {thinkingEnabled && (
                    <Box sx={{ mt: 1, maxWidth: 320 }}>
                      <TextField
                        fullWidth
                        label="Thinking Budget (tokens)"
                        type="number"
                        value={thinkingBudget}
                        onChange={(e) => setThinkingBudget(Number(e.target.value))}
                        inputProps={{ min: 100, max: 10000 }}
                        helperText="100 – 10,000 tokens"
                        size="small"
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Sampling Options */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sampling
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={samplingEnabled}
                    onChange={(e) => setSamplingEnabled(e.target.checked)}
                  />
                }
                label="Enable sampling"
              />

              {samplingEnabled && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="Sample Size"
                    type="number"
                    value={sampleSize}
                    onChange={(e) => setSampleSize(Number(e.target.value))}
                    inputProps={{ min: 1 }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Filter Query */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Filter (optional)
                </Typography>
                <Tooltip title="Pandas query syntax, e.g., ItemGrade == '4' or StudentScore > 2">
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <TextField
                fullWidth
                label="Filter Query"
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
                placeholder="ItemGrade == '4'"
                multiline
                rows={2}
              />
            </CardContent>
          </Card>

          {/* Action Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleRunEvaluation}
              disabled={isRunning || validationStatus !== 'valid' || !selectedDataset}
              sx={{ minWidth: 200 }}
            >
              {isRunning ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 1 }} />
                  Starting Evaluation...
                </>
              ) : (
                'Start Evaluation'
              )}
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Create Template Dialog */}
      <Dialog open={createTemplateOpen} onClose={() => setCreateTemplateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Prompt Template</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Template Name"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="my_custom_prompt"
              helperText="Letters, numbers, underscores, and hyphens only"
              fullWidth
            />
            <TextField
              label="Description"
              value={newTemplateDesc}
              onChange={(e) => setNewTemplateDesc(e.target.value)}
              placeholder="Short description of this template"
              fullWidth
            />
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Content — use these variables: <code>{'{grade}'}</code>, <code>{'{question}'}</code>, <code>{'{response}'}</code>, <code>{'{tot_points}'}</code>
              </Typography>
              <TextField
                label="Prompt Content"
                value={newTemplateContent}
                onChange={(e) => setNewTemplateContent(e.target.value)}
                multiline
                rows={12}
                fullWidth
                placeholder={`You are scoring a {grade} ELA response.\n\nQuestion: {question}\n\nStudent Response: {response}\n\nScore on a scale of 0 to {tot_points}.\n\nProvide your answer in JSON format with keys: score, explanation.`}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTemplateOpen(false)} disabled={savingTemplate}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateTemplate}
            disabled={savingTemplate || !newTemplateName.trim() || !newTemplateContent.trim()}
          >
            {savingTemplate ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Save Template
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CREvalRun;
